/* eslint-disable max-lines */
import { EventDetailsInterface } from '@elearning-platform/api-types/interfaces';
import {
  Agenda,
  AgendaDay,
  AgendaEvent,
  Breakpoint,
  Button,
  ButtonSize,
  ButtonTheme,
  Card,
  CardGroupCard,
  CardTheme,
  Color,
  EventTileDataType,
  EventTileDetailedData,
  EventTileLayout,
  FontWeight,
  FormButtonSize,
  FormButtonTheme,
  Icon,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  IconName,
  ItemsCarousel,
  LabelWithIconData,
  LabelWithIconGroup,
  Portfolio,
  Trainer as PortfolioTrainerData,
  TextButton,
  TextButtonTheme,
  TypographyVariant,
  useFormatDate,
  UseFormatDate,
} from '@elearning-platform/ui';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import React, { RefObject, useMemo, useRef } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Params, useParams } from 'react-router';

import { generalPhpApiDownloadFileRequest } from '../../api/requests';
import { EventFormDrawer } from '../../components';
import { RouteNameEnum, ViewImage } from '../../enums';
import { simpleDateFromTimestamp } from '../../helpers/date';
import { useEventDisenroll, useEventEnrollmentSubmit, useEventFormDrawerData, useLessonCard, useRedirectTo } from '../../hooks';
import { UseEventEnrollmentSubmit, UseEventFormDrawerData, UseLessonCard, UseRedirectTo } from '../../interfaces';

import BlocksImage from './images/blocks.svg';
import { useEventDetails, useTrainerMetrics } from './services/hooks';
import {
  AboutTrainingContent,
  AboutTrainingSection,
  AgendaSection,
  BannerRightSideWrapper,
  BannerSection,
  Blocks,
  BottomBar,
  BottomBarInnerBox,
  ButtonsContainer,
  CardsGroupSection,
  Container,
  Dot,
  Header,
  HeaderContent,
  PortfoliosSection,
  RaysBar,
  ScrollButton,
  ScrollButtonLabel,
  StickyBar,
  StyledFormButton,
  TopBar,
  TrainingDescription,
  TrainingGeneralText,
} from './trainingV2.styled';
import { Agenda as AgendaType, EventDetails, Trainer, TrainerMetric, UrlObject } from './trainingV2.types';

const TrainingV2: React.FC = (): JSX.Element => {
  const params: Params = useParams();
  const isSmallDesktop: boolean = useMediaQuery({
    query: Breakpoint.SmallDesktop,
  });
  const { redirect }: UseRedirectTo = useRedirectTo();
  const { t }: TransProps<never> = useTranslation();
  const aboutTrainingSectionRef: RefObject<HTMLElement> = useRef(null);
  const agendaSectionRef: RefObject<HTMLElement> = useRef(null);
  const trainersSectionRef: RefObject<HTMLElement> = useRef(null);
  const cardsSectionRef: RefObject<HTMLElement> = useRef(null);
  const { data: eventData }: UseQueryResult<EventDetails> = useEventDetails({ id: params.id });
  const { getTimestampMilisecValue }: UseFormatDate = useFormatDate();
  const { transformThumbnailIntoCardData }: UseLessonCard = useLessonCard({ withoutFavoriteButton: true });
  const trainersMetrics: TrainerMetric[] = useTrainerMetrics({ eventData })
    .filter((query: UseQueryResult<TrainerMetric, Error>): boolean | TrainerMetric => query.status === 'success' && query.data)
    .map((query: UseQueryResult<TrainerMetric, Error>): TrainerMetric => query.data as TrainerMetric);

  const { mutate: disenroll, isPending: isDisenrollPending }: UseMutationResult<void, Error, number> = useEventDisenroll();
  const { drawerSettings, eventData: eventDetails, survey, isFormLoading }: UseEventFormDrawerData = useEventFormDrawerData();
  const { handleEnrollSubmit, isSubmitting }: UseEventEnrollmentSubmit = useEventEnrollmentSubmit({
    id: eventDetails.id,
    attemptId: eventDetails.attemptId?.toString(),
    enrollSurvey: survey?.enrollSurvey,
  });

  const agendaDays: AgendaDay[] | undefined = useMemo(
    (): AgendaDay[] | undefined => {
      let result: AgendaDay[] = [];
      if (eventData?.agenda.length) {
        eventData.agenda.forEach((event: AgendaType): void => {
          if (!event.timestampStart || !event.timestampEnd) {
            return;
          }
          const newEvent: AgendaEvent = {
            start: event.timestampStart,
            end: event.timestampEnd,
            label: event.name,
          };
          const dayString: string = simpleDateFromTimestamp(event.timestampStart);
          const sameDay: AgendaDay | undefined = result.find((day: AgendaDay): boolean => day.date === dayString);
          if (!sameDay) {
            result = [...result, { date: dayString, events: [newEvent] }];
          } else {
            const sameDayWithNewEvent: AgendaDay = { ...sameDay, events: [...sameDay.events, newEvent] };
            result = result.map((item: AgendaDay): AgendaDay => item === sameDay ? sameDayWithNewEvent : item);
          }
        });
        if (result.every((day: AgendaDay): boolean => !!day.events.length)) {
          return result;
        }
      }

      return undefined;
    },
    [eventData]
  );

  const startDate: Dayjs | undefined = useMemo((): Dayjs | undefined => (
    eventData?.date.from ? dayjs.unix(eventData.date.from) : undefined
  ), [eventData]);

  const endDate: Dayjs | undefined = useMemo((): Dayjs | undefined => (
    eventData?.date.to ? dayjs.unix(eventData.date.to) : undefined
  ), [eventData]);

  const durationString: string | undefined = useMemo(
    (): string | undefined => {
      if (!startDate || !endDate) {
        return undefined;
      }
      const startOfStartDate: Dayjs = startDate.startOf('day');
      const startOfEndDate: Dayjs = endDate.startOf('day');

      if (startOfEndDate.diff(startOfStartDate, 'day') > 0) {
        return `${startOfEndDate.diff(startOfStartDate, 'day') + 1} ${t('competence.app.trainingV2.agenda.days')}`;
      }

      return `${endDate.diff(startDate, 'hour')}h`;
    },
    [startDate, endDate, t]
  );

  const generalAgenda: string[] | undefined = useMemo(
    (): string[] | undefined => {
      let transformedDates: string | undefined;
      if (!startDate || !endDate) {
        transformedDates = undefined;
      } else if (startDate.isSame(endDate, 'day')) {
        transformedDates = startDate.format('DD.MM.YYYY');
      } else if (startDate.isSame(endDate, 'month')) {
        transformedDates = `${startDate.format('D')}-${endDate.format('DD.MM.YYYY')}`;
      } else if (startDate.isSame(endDate, 'year')) {
        transformedDates = `${startDate.format('DD.MM')}-${endDate.format('DD.MM.YYYY')}`;
      } else {
        transformedDates = `${startDate.format('DD.MM.YYYY')}-${endDate.format('DD.MM.YYYY')}`;
      }

      if (agendaDays?.length) {
        return agendaDays.map((day: AgendaDay): string => (
          // eslint-disable-next-line @stylistic/max-len
          `${day.date} | ${dayjs.unix(day.events[0].start).format('HH:mm')} - ${dayjs.unix(day.events[day.events.length - 1].end).format('HH:mm')}`
        ));
      } else if (transformedDates) {
        return [transformedDates];
      }
    },
    [agendaDays, startDate, endDate]
  );

  const labels: LabelWithIconData[] | undefined = useMemo(
    (): LabelWithIconData[] | undefined => {
      if (!eventData) {
        return;
      }

      return [
        ...(eventData.course.category
          ? [{
              primaryText: t('competence.app.trainingV2.labels.type'),
              secondaryTexts: [eventData.course.category.enum === 'exam'
                ? t('competence.app.trainingV2.labels.exam')
                : t('competence.app.trainingV2.labels.training'),
              ],
              iconName: IconName.BookmarksOutlined,
            }]
          : []
        ),
        ...(durationString
          ? [{
              primaryText: t('competence.app.trainingV2.labels.duration'),
              secondaryTexts: [durationString],
              iconName: IconName.Time20s,
            }]
          : []
        ),
        ...(eventData.course.category
          ? [{
              primaryText: t('competence.app.trainingV2.labels.category'),
              secondaryTexts: [eventData.course.category.name],
              iconName: IconName.BookmarksOutlined,
            }]
          : []
        ),
        {
          primaryText: t('competence.app.trainingV2.labels.availableSpots'),
          secondaryTexts: [`${eventData.availableEnrolments} / ${eventData.totalEnrolments}`],
          iconName: IconName.GroupEqual,
        },
        ...(generalAgenda
          ? [{
              primaryText: t('competence.app.trainingV2.labels.dates'),
              secondaryTexts: generalAgenda,
              iconName: IconName.CalendarNote,
            }]
          : []
        ),
        ...(eventData.location
          ? [{
              primaryText: t('competence.app.trainingV2.labels.localization'),
              secondaryTexts: [
                `${eventData.location.name} ${eventData.location.address}, ${eventData.location.postcode} ${eventData.location.city}`,
              ],
              iconName: IconName.MeshGrid,
            }]
          : []
        ),
      ];
    },
    [eventData, durationString, generalAgenda, t]
  );

  const trainersWithoutMetricsData: PortfolioTrainerData[] = useMemo(
    (): PortfolioTrainerData[] => (
      eventData
        ? eventData.trainers
          .filter((trainer: Trainer): boolean => !trainer.hasMetric)
          .map((trainer: Trainer): PortfolioTrainerData => ({
            name: trainer.name,
            email: trainer.email ?? '',
          }))
        : []
    ), [eventData]
  );

  const fullTrainersData: PortfolioTrainerData[] | undefined = useMemo(
    (): PortfolioTrainerData[] | undefined => (
      [
        ...trainersMetrics.map((metric: TrainerMetric): PortfolioTrainerData => ({
          name: `${metric.name} ${metric.surname}`,
          email: metric.email ?? '',
          photo: metric.image,
          aboutMe: metric.aboutMe,
          prizes: metric.awards,
          additionalData: !!metric.trainingsCount && !!metric.workedYears && !!metric.specializations.length ? {
            numberOfTrainingsGiven: Number(metric.trainingsCount),
            yearsOfExperience: Number(metric.workedYears),
            specialization: metric.specializations[0].name,
          } : undefined,
        })),
        ...trainersWithoutMetricsData,
      ]
    ),
    [trainersMetrics, trainersWithoutMetricsData]
  );

  const scrollToRef: (ref: React.RefObject<HTMLElement>) => void = (ref: React.RefObject<HTMLElement>): void => {
    if (ref.current) {
      const stickyBarHeightOffset: number = 56;
      const elementPosition: number = ref.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementPosition - stickyBarHeightOffset, behavior: 'smooth' });
    }
  };

  const handleDownloadAction: (url: string) => void = (url: string): void => {
    void generalPhpApiDownloadFileRequest(url);
  };

  const mapEventDetailsToTileData: (event: EventDetailsInterface, layout: EventTileLayout) => EventTileDetailedData
  = (event: EventDetailsInterface, layout: EventTileLayout): EventTileDetailedData => ({
    type: EventTileDataType.Detailed,
    layout,
    img: event.course.image ?? ViewImage.TrainingsDefaultThumbnail,
    name: event.course.name,
    id: event.id,
    date: {
      from: getTimestampMilisecValue(event.date.from),
      to: getTimestampMilisecValue(event.date.to),
    },
    isAttending: event.userFlags?.isEnrolled,
    eventType: event.course.category?.enum === 'exams' ? 'exam' : 'event',
    location: event.location?.city,
    seats: { taken: event.availableEnrolments, total: event.totalEnrolments },
  });

  return (
    <Container>
      <EventFormDrawer
        eventData={eventDetails.details
          ? mapEventDetailsToTileData(eventDetails.details, EventTileLayout.Horizontal)
          : undefined
        }
        formSettings={{
          isFormLoading: isFormLoading || eventDetails.isLoading,
          isSubmitting,
          initialData: survey?.defaultValues,
          onSubmit: handleEnrollSubmit,
          addonsConfig: {
            withAccommodation: !!eventDetails.details?.entrySurvey?.withAccommodation,
            withParking: !!eventDetails.details?.entrySurvey?.withParking,
            withDinner: !!eventDetails.details?.entrySurvey?.withDinner,
            withVege: !!eventDetails.details?.entrySurvey?.withVege,
          },
        }}
        isOpen={drawerSettings.isOpen}
        onClose={drawerSettings.closeDrawer}
      />
      <TopBar>
        <TextButton
          icon={IconName.ChevronLeft}
          label={t('competence.app.trainingV2.backToListButton')}
          onClick={(): void => redirect({ route: RouteNameEnum.Trainings })}
          variant={TextButtonTheme.TertiaryWhite}
        />
      </TopBar>
      {!!eventData && (
        <>
          {!isSmallDesktop && (
            <HeaderContent>
              <Header variant={TypographyVariant.DisplayXS}>
                {eventData?.course.name}
              </Header>
            </HeaderContent>
          )}
          <BannerSection $backgroundImage={eventData.course.image}>
            {!!isSmallDesktop && (
              <HeaderContent>
                <Header variant={TypographyVariant.DisplayLG}>
                  {eventData?.course.name}
                </Header>
              </HeaderContent>
            )}
            <BannerRightSideWrapper>
              {!!(eventData.userFlags.isEnrolled && eventData.entrySurvey?.attemptId && !isSmallDesktop) && (
                <StyledFormButton
                  icon={IconName.Edit}
                  label={(t('competence.app.trainingV2.editEnrollment'))}
                  onClick={(): void => params.id ? drawerSettings.openDrawer(params.id) : undefined}
                  reversed
                  size={FormButtonSize.Medium}
                  variant={FormButtonTheme.SecondaryWhiteDouble}
                />
              )}
              {!!labels && (
                <LabelWithIconGroup labels={labels} />
              )}
              <ButtonsContainer>
                {!!eventData.userFlags.canBeEnrolled && (
                  <Button
                    label={t('competence.app.trainingV2.enrollButton')}
                    onClick={(): void => params.id ? drawerSettings.openDrawer(params.id) : undefined}
                    size={isSmallDesktop ? ButtonSize.Large : ButtonSize.Medium}
                    variant={ButtonTheme.PrimaryBrand}
                  />
                )}
                {!!eventData.userFlags.canBeDisenrolled && (
                  <Button
                    isLoading={isDisenrollPending}
                    label={t('competence.app.trainingV2.disenrollButton')}
                    onClick={(): void => Number(params.id) ? disenroll(Number(params.id)) : undefined}
                    size={isSmallDesktop ? ButtonSize.Large : ButtonSize.Medium}
                    variant={ButtonTheme.PrimaryBrand}
                  />
                )}
                {!!eventData.urls.length
                  && eventData.urls
                    .filter((item: UrlObject): item is Required<UrlObject> => !!item.url && !!item.label)
                    .map((item: Required<UrlObject>): JSX.Element => (
                      <Button
                        key={item.url}
                        label={t('competence.app.trainingV2.downloadAttendeesList')}
                        onClick={(): void => handleDownloadAction(item.url)}
                        size={isSmallDesktop ? ButtonSize.Large : ButtonSize.Medium}
                        variant={ButtonTheme.PrimaryWhite}
                      />
                    ))
                }
                {!!(eventData.userFlags.isEnrolled && eventData.entrySurvey?.attemptId && isSmallDesktop) && (
                  <IconButton
                    icon={IconName.Edit}
                    onClick={(): void => params.id ? drawerSettings.openDrawer(params.id) : undefined}
                    size={IconButtonSize.Large}
                    variant={IconButtonVariant.PrimaryWhite}
                  />
                )}
              </ButtonsContainer>
            </BannerRightSideWrapper>
          </BannerSection>
          <StickyBar>
            <ScrollButton onClick={(): void => scrollToRef(aboutTrainingSectionRef)}>
              <ScrollButtonLabel fontWeight={FontWeight.Bold} variant={TypographyVariant.Subheader}>
                {t('competence.app.trainingV2.scrollToAboutTraining')}
              </ScrollButtonLabel>
            </ScrollButton>
            {!!agendaDays && (
              <>
                <Dot />
                <ScrollButton onClick={(): void => scrollToRef(agendaSectionRef)}>
                  <ScrollButtonLabel fontWeight={FontWeight.Bold} variant={TypographyVariant.Subheader}>
                    {t('competence.app.trainingV2.scrollToAgenda')}
                  </ScrollButtonLabel>
                </ScrollButton>
              </>
            )}
            {!!eventData.sources?.elements?.length && (
              <>
                <Dot />
                <ScrollButton onClick={(): void => scrollToRef(cardsSectionRef)}>
                  <ScrollButtonLabel fontWeight={FontWeight.Bold} variant={TypographyVariant.Subheader}>
                    {t('competence.app.trainingV2.scrollToCards')}
                  </ScrollButtonLabel>
                </ScrollButton>
              </>
            )}
            {!!fullTrainersData?.length && (
              <>
                <Dot />
                <ScrollButton onClick={(): void => scrollToRef(trainersSectionRef)}>
                  <ScrollButtonLabel fontWeight={FontWeight.Bold} variant={TypographyVariant.Subheader}>
                    {t('competence.app.trainingV2.scrollToMetrics')}
                  </ScrollButtonLabel>
                </ScrollButton>
              </>
            )}
          </StickyBar>
          <RaysBar />
          <AboutTrainingSection ref={aboutTrainingSectionRef}>
            <Icon color={Color.Brand100} name={IconName.Flare} size={20} />
            <AboutTrainingContent>
              <TrainingGeneralText
                fontWeight={FontWeight.SemiBold}
                variant={isSmallDesktop ? TypographyVariant.DisplaySM : TypographyVariant.DisplayXS}
              >
                {eventData.course.header ?? t('competence.app.trainingV2.generalDescription')}
              </TrainingGeneralText>
              {!!eventData && (
                <TrainingDescription variant={TypographyVariant.TextSM}>
                  {eventData.course.description}
                </TrainingDescription>
              )}
            </AboutTrainingContent>
          </AboutTrainingSection>
          {!!agendaDays && (
            <AgendaSection ref={agendaSectionRef}>
              <Agenda days={agendaDays} />
              {!!isSmallDesktop && <Blocks src={BlocksImage} />}
            </AgendaSection>
          )}
          {!!eventData.sources?.elements.length && (
            <CardsGroupSection ref={cardsSectionRef}>
              <ItemsCarousel
                headlineData={{
                  headline: t('competence.app.trainingV2.cardsSection'),
                  headlineColor: Color.Gray100,
                }}
                items={eventData.sources.elements.map(transformThumbnailIntoCardData)}
                renderItem={(item: CardGroupCard): JSX.Element => (
                  <Card
                    {...item}
                    cardTheme={CardTheme.Light}
                    tagsData={{
                      list: item.tags,
                      colorsData: {
                        backgroundColor: Color.Gray100,
                        textColor: Color.Gray600,
                      },
                    }}
                  />
                )}
              />
            </CardsGroupSection>
          )}
          {!!fullTrainersData?.length && (
            <PortfoliosSection ref={trainersSectionRef}>
              <Portfolio trainers={fullTrainersData} />
            </PortfoliosSection>
          )}
          <BottomBar>
            <BottomBarInnerBox />
          </BottomBar>
        </>
      )}
    </Container>
  );
};

export default TrainingV2;
